/**
 * Dangling conditional-logic and scoring references.
 *
 * These are the PRD Error 6 examples that do not block save today. Nothing
 * cleans up a rule when the field, option or slide it points at is deleted, so
 * the rule silently stops firing and the builder looks fine. They are
 * Recommendations rather than Errors: making them blocking would stop existing
 * published assets from being saved at all, which Backward Compatibility rules
 * out.
 */
import type { BuilderContext, RuleResult, ValidationRule } from "../types";

const ALL_ASSETS: ValidationRule["assetTypes"] = ["form", "survey", "quiz"];
const SLIDE_ASSETS: ValidationRule["assetTypes"] = ["survey", "quiz"];

const text = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

interface Condition {
  selectedField?: string;
  selectedOperation?: string;
  inputValue?: string;
}

interface Outcome {
  type?: string;
  value?: string;
  jumpTarget?: string;
}

interface Step {
  conditions?: Condition[];
  outcome?: Outcome;
}

const steps = (ctx: BuilderContext): Step[] => {
  const rules = ctx.builderConfig.conditionalLogic;
  return Array.isArray(rules) ? (rules as Step[]) : [];
};

/** Conditions address fields by query key. */
const fieldKeys = (ctx: BuilderContext): Set<string> => {
  const keys = new Set<string>();
  ctx.elements.forEach(({ element }) => {
    const key = text(element.hiddenFieldQueryKey);
    if (key) keys.add(key);
  });
  return keys;
};

const slideIdSet = (ctx: BuilderContext): Set<string> => new Set(ctx.slideIds);

const logicResult = (ruleId: string, index: number): RuleResult => ({
  ruleId,
  severity: "recommendation",
  section: "logic",
  key: `${ruleId}:${index}`,
  target: { settingsTab: "logic" },
});

export const logicOrphanFieldReference: ValidationRule = {
  id: "logic_orphan_field_reference",
  severity: "recommendation",
  assetTypes: ALL_ASSETS,
  section: "logic",
  evaluate: (ctx) => {
    const keys = fieldKeys(ctx);
    const slides = slideIdSet(ctx);
    return steps(ctx)
      .map((step, index) => ({ step, index }))
      .filter(({ step }) =>
        (step.conditions ?? []).some((condition) => {
          const field = text(condition.selectedField);
          if (!field) return false;
          // Slide-basis Jump To addresses a slide, not a field.
          if (slides.has(field)) return false;
          return !keys.has(field);
        })
      )
      .map(({ index }) => logicResult("logic_orphan_field_reference", index));
  },
};

/**
 * An option-valued condition whose value no longer exists among the field's
 * options. Only checked for conditions that name a field we can still find —
 * a missing field is the other rule's finding.
 */
export const logicOrphanOptionReference: ValidationRule = {
  id: "logic_orphan_option_reference",
  severity: "recommendation",
  assetTypes: ALL_ASSETS,
  section: "logic",
  evaluate: (ctx) => {
    const optionsByKey = new Map<string, Set<string>>();
    ctx.elements.forEach(({ element }) => {
      const key = text(element.hiddenFieldQueryKey);
      if (!key || !Array.isArray(element.picklistOptions)) return;
      const labels = element.picklistOptions.map((option) =>
        text(typeof option === "string" ? option : option?.label)
      );
      optionsByKey.set(key, new Set(labels.filter(Boolean)));
    });

    return steps(ctx)
      .map((step, index) => ({ step, index }))
      .filter(({ step }) =>
        (step.conditions ?? []).some((condition) => {
          const field = text(condition.selectedField);
          const value = text(condition.inputValue);
          const options = optionsByKey.get(field);
          if (!options || !value) return false;
          return !options.has(value);
        })
      )
      .map(({ index }) => logicResult("logic_orphan_option_reference", index));
  },
};

export const logicOrphanTarget: ValidationRule = {
  id: "logic_orphan_target",
  severity: "recommendation",
  assetTypes: SLIDE_ASSETS,
  section: "logic",
  evaluate: (ctx) => {
    const slides = slideIdSet(ctx);
    if (!slides.size) return [];
    return steps(ctx)
      .map((step, index) => ({ step, index }))
      .filter(({ step }) => {
        const target = text(step.outcome?.jumpTarget);
        return Boolean(target) && !slides.has(target);
      })
      .map(({ index }) => logicResult("logic_orphan_target", index));
  },
};

/**
 * A score calculation naming a field that no longer exists. The editor renders
 * the stale key verbatim, so the builder shows something that looks configured.
 */
export const scoreCalculationInvalidReference: ValidationRule = {
  id: "score_calculation_invalid_reference",
  severity: "recommendation",
  assetTypes: ALL_ASSETS,
  section: "questions",
  evaluate: (ctx) => {
    const keys = fieldKeys(ctx);
    return ctx.elements
      .filter((entry) => entry.element.dataType === "SCORE")
      .filter((entry) => {
        const calculation = (entry.element as { calculation?: unknown })
          .calculation;
        if (!Array.isArray(calculation)) return false;
        return calculation.some((token) => {
          const value = text(token);
          // Operators and numbers are not field references.
          if (!value || /^[\d\s()+\-*/.]+$/.test(value)) return false;
          return !keys.has(value);
        });
      })
      .map((entry) => ({
        ruleId: "score_calculation_invalid_reference",
        severity: "recommendation" as const,
        section: "questions" as const,
        key: `score_calculation_invalid_reference:${entry.elementKey}`,
        target: {
          elementIndex: entry.elementIndex,
          slideIndex: entry.slideIndex,
          elementKey: entry.elementKey,
        },
      }));
  },
};

export const logicRecommendationRules: ValidationRule[] = [
  logicOrphanFieldReference,
  logicOrphanOptionReference,
  logicOrphanTarget,
  scoreCalculationInvalidReference,
];
